// Generated by ReScript, PLEASE EDIT WITH CARE
'use strict';

var Curry = require("rescript/lib/js/curry.js");
var React = require("react");
var Belt_List = require("rescript/lib/js/belt_List.js");
var Belt_Option = require("rescript/lib/js/belt_Option.js");
var Caml_option = require("rescript/lib/js/caml_option.js");
var JsxRuntime = require("react/jsx-runtime");
var Utils$ReactHooksTemplate = require("../common/utils.res.js");

var helpers = ((function() {
    var tokenCharacter = /[a-zA-Z0-9_]/;

    function isToken(node) {
      return node && node.nodeType === 1 && node.hasAttribute("data-placeholder-token");
    }

    function serialize(node) {
      if (!node) return "";
      if (node.nodeType === 3) return (node.nodeValue || "").replace(/\u00a0/g, " ");
      if (isToken(node)) return node.getAttribute("data-placeholder-token") || "";
      if (node.nodeType === 1 && node.tagName === "BR") return "\n";

      var value = "";
      for (var child = node.firstChild; child; child = child.nextSibling) {
        value += serialize(child);
      }

      if (
        node.nodeType === 1 &&
        /^(DIV|P|LI)$/.test(node.tagName) &&
        node.nextSibling &&
        value.slice(-1) !== "\n"
      ) {
        value += "\n";
      }
      return value;
    }

    function tokenPattern(tokens) {
      if (!tokens.length) return null;
      var escaped = tokens
        .slice()
        .sort(function(a, b) { return b.length - a.length; })
        .map(function(token) { return token.replace(/[|\\{}()[\]^$+*?.-]/g, "\\$&"); });
      return new RegExp("(" + escaped.join("|") + ")(?![a-zA-Z0-9_])", "g");
    }

    function appendTextWithTokens(editor, text, tokens, offset) {
      var pattern = tokenPattern(tokens);
      if (!pattern) {
        editor.appendChild(document.createTextNode(text));
        return;
      }

      var cursor = 0;
      var match;
      while ((match = pattern.exec(text)) !== null) {
        if (match.index > cursor) {
          editor.appendChild(document.createTextNode(text.slice(cursor, match.index)));
        }

        var token = match[0];
        var chip = document.createElement("span");
        chip.setAttribute("data-placeholder-token", token);
        chip.setAttribute("data-placeholder-start", String(offset + match.index));
        chip.setAttribute("contenteditable", "false");
        chip.className =
          "inline-flex items-center gap-1 px-2 py-0.5 mx-0.5 align-baseline " +
          "text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded";

        var label = document.createElement("span");
        label.textContent = token;
        chip.appendChild(label);

        var remove = document.createElement("span");
        remove.setAttribute("role", "button");
        remove.setAttribute("data-placeholder-remove", "true");
        remove.setAttribute("aria-label", "Remove placeholder " + token);
        remove.title = "Remove " + token;
        remove.className =
          "inline-flex items-center justify-center w-4 h-4 -mr-1 rounded cursor-pointer " +
          "text-blue-500 hover:text-blue-800 hover:bg-blue-100";
        remove.textContent = "\u00d7";
        chip.appendChild(remove);
        editor.appendChild(chip);
        cursor = match.index + token.length;
      }

      if (cursor < text.length) {
        editor.appendChild(document.createTextNode(text.slice(cursor)));
      }
    }

    function render(editor, value, tokens, multiline) {
      while (editor.firstChild) editor.removeChild(editor.firstChild);
      var lines = multiline ? value.split("\n") : [value.replace(/[\r\n]+/g, " ")];
      var offset = 0;
      lines.forEach(function(line, index) {
        appendTextWithTokens(editor, line, tokens, offset);
        offset += line.length;
        if (index < lines.length - 1) {
          editor.appendChild(document.createElement("br"));
          offset += 1;
        }
      });
    }

    function selectionOffsets(editor) {
      var selection = window.getSelection();
      var fallback = serialize(editor).length;
      if (!selection || selection.rangeCount === 0) return {start: fallback, end: fallback};

      var range = selection.getRangeAt(0);
      if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) {
        return {start: fallback, end: fallback};
      }

      function offsetTo(container, offset) {
        var prefix = range.cloneRange();
        prefix.selectNodeContents(editor);
        prefix.setEnd(container, offset);
        return serialize(prefix.cloneContents()).length;
      }

      return {
        start: offsetTo(range.startContainer, range.startOffset),
        end: offsetTo(range.endContainer, range.endOffset)
      };
    }

    function setCaret(editor, target) {
      var range = document.createRange();
      var selection = window.getSelection();
      var consumed = 0;
      var placed = false;

      function visit(node) {
        if (placed) return;
        if (node.nodeType === 3) {
          var length = (node.nodeValue || "").length;
          if (target <= consumed + length) {
            range.setStart(node, Math.max(0, target - consumed));
            placed = true;
            return;
          }
          consumed += length;
          return;
        }
        if (isToken(node)) {
          var tokenLength = (node.getAttribute("data-placeholder-token") || "").length;
          if (target <= consumed) {
            range.setStartBefore(node);
            placed = true;
          } else if (target <= consumed + tokenLength) {
            range.setStartAfter(node);
            placed = true;
          }
          consumed += tokenLength;
          return;
        }
        if (node.nodeType === 1 && node.tagName === "BR") {
          if (target <= consumed) {
            range.setStartBefore(node);
            placed = true;
          }
          consumed += 1;
          return;
        }
        for (var child = node.firstChild; child; child = child.nextSibling) visit(child);
      }

      visit(editor);
      if (!placed) range.selectNodeContents(editor), range.collapse(false);
      else range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    function findAdjacentToken(value, tokens, caret, key) {
      for (var index = 0; index < tokens.length; index += 1) {
        var token = tokens[index];
        if (key === "Backspace" && caret >= token.length) {
          var start = caret - token.length;
          if (
            value.slice(start, caret) === token &&
            (caret === value.length || !tokenCharacter.test(value.charAt(caret)))
          ) return {start: start, end: caret};
        }
        if (
          key === "Delete" &&
          value.slice(caret, caret + token.length) === token &&
          (caret + token.length === value.length ||
            !tokenCharacter.test(value.charAt(caret + token.length)))
        ) return {start: caret, end: caret + token.length};
      }
      return null;
    }

    return {
      read: function(editor) {
        return serialize(editor).replace(/\n$/, "");
      },
      render: render,
      normalize: function(editor, value, tokens, multiline) {
        var current = serialize(editor).replace(/\n$/, "");
        if (!multiline) {
          current = current.replace(/[\r\n]+/g, " ");
        }
        // Rebuild chips from the live editor text (covers manually typed $tokens).
        render(editor, current, tokens, multiline);
        return current;
      },
      insert: function(editor, value, tokens, token, multiline) {
        value = serialize(editor);
        var offsets = selectionOffsets(editor);
        var suffix = value.slice(offsets.end);
        var separator = suffix.length && tokenCharacter.test(suffix.charAt(0)) ? " " : "";
        var nextValue = value.slice(0, offsets.start) + token + separator + suffix;
        var caret = offsets.start + token.length + separator.length;
        render(editor, nextValue, tokens, multiline);
        editor.focus();
        setCaret(editor, caret);
        return {value: nextValue, caret: caret};
      },
      atomicDelete: function(editor, value, tokens, key, multiline) {
        if (key !== "Backspace" && key !== "Delete") return null;
        value = serialize(editor).replace(/\n$/, "");
        var offsets = selectionOffsets(editor);
        if (offsets.start !== offsets.end) return null;
        var bounds = findAdjacentToken(value, tokens, offsets.start, key);
        if (!bounds) return null;
        var nextValue = value.slice(0, bounds.start) + value.slice(bounds.end);
        render(editor, nextValue, tokens, multiline);
        setCaret(editor, bounds.start);
        return {value: nextValue, caret: bounds.start};
      },
      removeClicked: function(editor, event, value, tokens, multiline) {
        var target = event.target && event.target.closest
          ? event.target.closest("[data-placeholder-remove]")
          : null;
        if (!target || !editor.contains(target)) return null;
        // Keep focus in the editor so blur/normalize cannot rebuild the DOM mid-remove.
        event.preventDefault();
        event.stopPropagation();
        var chip = target.closest("[data-placeholder-token]");
        if (!chip) return null;
        value = serialize(editor).replace(/\n$/, "");
        var prefix = document.createRange();
        prefix.selectNodeContents(editor);
        prefix.setEndBefore(chip);
        var start = serialize(prefix.cloneContents()).length;
        var token = chip.getAttribute("data-placeholder-token") || "";
        var nextValue = value.slice(0, start) + value.slice(start + token.length);
        render(editor, nextValue, tokens, multiline);
        editor.focus();
        setCaret(editor, start);
        return {value: nextValue, caret: start};
      }
    };
  })());

function supportedTokens(placeholderChips) {
  return Belt_List.toArray(Belt_List.map(placeholderChips, (function (chip) {
                    return chip.token;
                  })));
}

var asSingleLine = (function(value) {
  return value.replace(/[\r\n]+/g, " ");
});

function withEditor(editorRef, callback) {
  var editor = editorRef.current;
  if (!(editor == null)) {
    return Curry._1(callback, editor);
  }
  
}

function insertPlaceholder(editorRef, value, token, placeholderChips, multiline) {
  var editor = editorRef.current;
  if (editor == null) {
    return value + token;
  } else {
    return helpers.insert(editor, value, supportedTokens(placeholderChips), token, multiline).value;
  }
}

function PlaceholderEditor(props) {
  var __multiline = props.multiline;
  var placeholderChips = props.placeholderChips;
  var editorRef = props.editorRef;
  var onFocus = props.onFocus;
  var onChange = props.onChange;
  var value = props.value;
  var multiline = __multiline !== undefined ? __multiline : false;
  React.useEffect((function (param) {
          withEditor(editorRef, (function (editor) {
                  if (helpers.read(editor) !== value) {
                    helpers.render(editor, value, supportedTokens(placeholderChips), multiline);
                    return ;
                  }
                  
                }));
        }), [
        value,
        multiline
      ]);
  var updateFromResult = function (result) {
    if (!(result == null)) {
      return Curry._1(onChange, result.value);
    }
    
  };
  return JsxRuntime.jsxs("div", {
              children: [
                value === "" ? JsxRuntime.jsx("span", {
                        children: props.placeholder,
                        className: "pointer-events-none absolute left-4 top-2 text-sm text-gray-400"
                      }) : null,
                JsxRuntime.jsx("div", {
                      ref: Caml_option.some(editorRef),
                      "aria-multiline": multiline,
                      className: "w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition duration-150 whitespace-pre-wrap break-words " + (
                        multiline ? "min-h-[100px]" : "min-h-[38px] whitespace-nowrap overflow-x-auto"
                      ),
                      contentEditable: true,
                      id: props.id,
                      role: "textbox",
                      onPaste: (function ($$event) {
                          if (multiline) {
                            return Utils$ReactHooksTemplate.pasteAsPlainText($$event);
                          } else {
                            return Utils$ReactHooksTemplate.pasteAsSingleLinePlainText($$event);
                          }
                        }),
                      onKeyDown: (function ($$event) {
                          var key = $$event.key;
                          if (!multiline && key === "Enter") {
                            $$event.preventDefault();
                            return ;
                          } else {
                            return withEditor(editorRef, (function (editor) {
                                          var result = helpers.atomicDelete(editor, value, supportedTokens(placeholderChips), key, multiline);
                                          if (Belt_Option.isSome((result == null) ? undefined : Caml_option.some(result))) {
                                            $$event.preventDefault();
                                            return updateFromResult(result);
                                          }
                                          
                                        }));
                          }
                        }),
                      onFocus: (function (param) {
                          Curry._1(onFocus, undefined);
                        }),
                      onBlur: (function (param) {
                          withEditor(editorRef, (function (editor) {
                                  var nextValue = helpers.normalize(editor, value, supportedTokens(placeholderChips), multiline);
                                  if (nextValue !== value) {
                                    return Curry._1(onChange, nextValue);
                                  }
                                  
                                }));
                        }),
                      onInput: (function (param) {
                          withEditor(editorRef, (function (editor) {
                                  var nextValue = helpers.read(editor);
                                  Curry._1(onChange, multiline ? nextValue : asSingleLine(nextValue));
                                }));
                        }),
                      onMouseDown: (function ($$event) {
                          withEditor(editorRef, (function (editor) {
                                  updateFromResult(helpers.removeClicked(editor, $$event, value, supportedTokens(placeholderChips), multiline));
                                }));
                        }),
                      suppressContentEditableWarning: true
                    })
              ],
              className: "relative"
            });
}

var make = PlaceholderEditor;

exports.helpers = helpers;
exports.supportedTokens = supportedTokens;
exports.asSingleLine = asSingleLine;
exports.withEditor = withEditor;
exports.insertPlaceholder = insertPlaceholder;
exports.make = make;
/* helpers Not a pure module */
